import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  adminGetAgentSubscription,
  adminListAgentSubscriptions,
  adminPatchAgentSubscription,
  getApiError,
} from '../../lib/api';
import { toast } from 'sonner';
import { Pencil, RefreshCw, Loader2, Layers } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';

function parseListResponse(res) {
  const d = res?.data;
  if (!d) return [];
  if (Array.isArray(d)) return d;
  return d.subscriptions || d.items || d.results || d.rows || [];
}

/** Backend can send `business_name` or nested customer/business objects — see docs/agent-subscriptions-admin-api.md */
function pickBusinessName(row) {
  if (!row || typeof row !== 'object') return '';
  const direct =
    row.business_name ??
    row.businessName ??
    row.customer_name ??
    row.customerName;
  if (direct != null && String(direct).trim() !== '') return String(direct).trim();
  const nest =
    row.customer?.name ??
    row.customer?.business_name ??
    row.business?.name ??
    row.user?.name ??
    row.user?.business_name;
  if (nest != null && String(nest).trim() !== '') return String(nest).trim();
  return '';
}

/** Keys allowed by backend PATCH (see API docs). */
const PATCH_FIELD_GROUPS = [
  {
    title: 'Voice minutes',
    fields: [
      ['minutes_total', 'Minutes total'],
      ['minutes_remaining', 'Minutes remaining'],
      ['minutes_used', 'Minutes used'],
    ],
  },
  {
    title: 'General messages',
    fields: [
      ['total_message_count', 'Total messages'],
      ['remaining_message_count', 'Remaining messages'],
      ['used_message_count', 'Used messages'],
    ],
  },
  {
    title: 'Facebook messages',
    fields: [
      ['total_facebook_message_count', 'Total Facebook'],
      ['remaining_facebook_message_count', 'Remaining Facebook'],
      ['used_facebook_message_count', 'Used Facebook'],
    ],
  },
  {
    title: 'Add-ons',
    fields: [
      ['addon_minutes_expiring', 'Add-on minutes expiring'],
      ['addon_messages_expiring', 'Add-on messages expiring'],
      ['addon_credits_expire_at', 'Add-on credits expire at (ISO)'],
    ],
  },
  {
    title: 'Status & plan',
    fields: [
      ['status', 'Status'],
      ['stripe_status', 'Stripe status'],
      ['plan_id', 'Plan ID'],
      ['plan_name', 'Plan name'],
      ['price', 'Price'],
      ['billing_cycle', 'Billing cycle'],
    ],
  },
  {
    title: 'Stripe',
    fields: [['stripe_subscription_id', 'Stripe subscription ID']],
  },
  {
    title: 'Dates (ISO 8601)',
    fields: [
      ['renews_at', 'Renews at'],
      ['current_period_end', 'Current period end'],
      ['expires_at', 'Expires at'],
      ['started_at', 'Started at'],
      ['last_renewed_at', 'Last renewed at'],
      ['cancelled_at', 'Cancelled at'],
      ['replaced_at', 'Replaced at'],
    ],
  },
];

function SubscriptionEditForm({ form, setForm }) {
  return (
    <ScrollArea className="max-h-[min(70vh,560px)] pr-4">
      <div className="space-y-6 pb-4">
        {PATCH_FIELD_GROUPS.map((group) => (
          <div key={group.title} className="space-y-3">
            <p className="text-sm font-semibold text-slate-800">{group.title}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {group.fields.map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-xs font-normal text-slate-600">{label}</Label>
                  <Input
                    value={form[key] ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    placeholder="—"
                    className="font-mono text-xs h-8"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

function buildPatchPayload(form, originalDoc) {
  const payload = {};
  const numKeys = new Set([
    'minutes_total',
    'minutes_remaining',
    'minutes_used',
    'total_message_count',
    'remaining_message_count',
    'used_message_count',
    'total_facebook_message_count',
    'remaining_facebook_message_count',
    'used_facebook_message_count',
    'addon_minutes_expiring',
    'addon_messages_expiring',
    'price',
  ]);

  for (const key of Object.keys(form)) {
    const raw = form[key];
    const trimmed = raw === undefined || raw === null ? '' : String(raw).trim();
    if (trimmed === '') continue;
    if (originalDoc?.[key] !== undefined && String(originalDoc[key]) === trimmed) continue;

    if (numKeys.has(key)) {
      const n = Number(trimmed);
      if (Number.isFinite(n)) payload[key] = n;
    } else {
      payload[key] = trimmed;
    }
  }
  return payload;
}

function rowToForm(sub) {
  const out = {};
  const allKeys = PATCH_FIELD_GROUPS.flatMap((g) => g.fields.map(([k]) => k));
  for (const key of allKeys) {
    const v = sub?.[key];
    if (v === undefined || v === null) out[key] = '';
    else if (typeof v === 'object') out[key] = JSON.stringify(v);
    else out[key] = String(v);
  }
  return out;
}

export default function AdminAgentSubscriptionsPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    agent_id: '',
    customer_id: '',
    status: '',
    subscription_id: '',
    limit: '25',
    skip: '0',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const [form, setForm] = useState({});

  async function fetchSubscriptions() {
    try {
      setLoading(true);
      const params = {};
      if (filters.agent_id.trim()) params.agent_id = filters.agent_id.trim();
      if (filters.customer_id.trim()) params.customer_id = filters.customer_id.trim();
      if (filters.status.trim()) params.status = filters.status.trim();
      if (filters.subscription_id.trim()) params.subscription_id = filters.subscription_id.trim();
      params.limit = Math.min(500, Math.max(1, parseInt(filters.limit, 10) || 25));
      params.skip = Math.max(0, parseInt(filters.skip, 10) || 0);
      const res = await adminListAgentSubscriptions(params);
      setRows(parseListResponse(res));
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Failed to load subscriptions');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const openEdit = async (row) => {
    const sid = row.subscription_id || row.id || row.agent_subscription_id;
    if (!sid) {
      toast.error('Missing subscription id on row');
      return;
    }
    try {
      const res = await adminGetAgentSubscription(sid);
      const sub = res?.data?.subscription || res?.data || row;
      setEditSub(sub);
      setForm(rowToForm(sub));
      setDialogOpen(true);
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Failed to load subscription');
    }
  };

  const handleSaveEdit = async () => {
    if (!editSub) return;
    const sid =
      editSub.subscription_id || editSub.id || editSub.agent_subscription_id;
    const payload = buildPatchPayload(form, editSub);
    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save');
      return;
    }
    try {
      setSaving(true);
      await adminPatchAgentSubscription(sid, payload);
      toast.success('Subscription updated');
      setDialogOpen(false);
      setEditSub(null);
      await fetchSubscriptions();
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Patch failed');
    } finally {
      setSaving(false);
    }
  };

  const applyFilters = () => {
    fetchSubscriptions();
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-agent-subscriptions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-jakarta font-bold text-slate-900 flex items-center gap-2">
            <Layers className="w-7 h-7 text-teal-600" />
            Agent subscriptions
          </h1>
          <p className="text-slate-500 mt-1">
            List and manually adjust agent subscription rows (quota, status, Facebook pool, dates).
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => fetchSubscriptions()} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>Query parameters for GET /api/admin/agent-subscriptions</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {[
            ['agent_id', 'Agent ID'],
            ['customer_id', 'Customer ID'],
            ['status', 'Status'],
            ['subscription_id', 'Subscription ID'],
            ['limit', 'Limit'],
            ['skip', 'Skip'],
          ].map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs">{label}</Label>
              <Input
                value={filters[k]}
                onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value }))}
                className="h-9 text-sm"
              />
            </div>
          ))}
          <div className="flex items-end">
            <Button type="button" className="w-full bg-teal-600 hover:bg-teal-700" onClick={applyFilters}>
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-slate-500">No subscriptions match the current filters.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Customer ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Msgs left</TableHead>
                  <TableHead>FB left</TableHead>
                  <TableHead>Renews</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, idx) => {
                  const sid = row.subscription_id || row.id || `row-${idx}`;
                  return (
                    <TableRow key={sid}>
                      <TableCell className="font-mono text-xs max-w-[200px] break-all">{sid}</TableCell>
                      <TableCell
                        className="text-sm text-slate-800 max-w-[min(18rem,40vw)] truncate"
                        title={pickBusinessName(row) || undefined}
                      >
                        {pickBusinessName(row) || '—'}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{row.agent_id || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{row.customer_id || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{row.status || '—'}</Badge>
                      </TableCell>
                      <TableCell>{row.remaining_message_count ?? '—'}</TableCell>
                      <TableCell>{row.remaining_facebook_message_count ?? '—'}</TableCell>
                      <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                        {row.renews_at ? formatDateTime(row.renews_at) : '—'}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" type="button" onClick={() => openEdit(row)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit subscription</DialogTitle>
          </DialogHeader>
          {editSub && (
            <>
              {pickBusinessName(editSub) ? (
                <p className="text-sm font-medium text-slate-800">
                  Business: {pickBusinessName(editSub)}
                </p>
              ) : null}
              <p className="text-xs text-slate-500 font-mono break-all">
                {editSub.subscription_id || editSub.id}
              </p>
              <SubscriptionEditForm form={form} setForm={setForm} />
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={handleSaveEdit}
                  disabled={saving}
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save changes
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
